// frontend/src/store/projectStore.js
// Store de Zustand para gestionar el estado de los proyectos, fases y actividades.
// CORREGIDO: Manejo de la respuesta de la sincronización.
// ACTUALIZADO: Añadido estado y acciones para los logs de auditoría de proyectos.

import { create } from 'zustand';
import {
  createProject as apiCreateProject,
  getAllProjects as apiGetAllProjects,
  getProjectById as apiGetProjectById,
  updateProject as apiUpdateProject,
  deleteProject as apiDeleteProject,
  updatePhaseActivity as apiUpdatePhaseActivity,
  syncProjectActivities as apiSyncProjectActivities,
  getBaseActivities as apiGetBaseActivities,
  addBaseActivity as apiAddBaseActivity,
  updateBaseActivity as apiUpdateBaseActivity,
  deleteBaseActivity as apiDeleteBaseActivity,
  getProjectAuditLogs as apiGetProjectAuditLogs, // NUEVO: Importar la función de API para logs
} from '../api/project';
import {
  uploadFile as apiUploadFile,
  addComment as apiAddComment,
  deleteFile as apiDeleteFile
} from '../api/fileComment';
import { useAuthStore } from './authStore';
import { useUIStore } from './uiStore';

/**
 * @typedef {object} ProjectStoreState
 * @property {Array<object>} projects - Lista de proyectos.
 * @property {boolean} isLoadingProjects - Indica si se están cargando los proyectos.
 * @property {object|null} selectedProject - El proyecto seleccionado para ver/editar.
 * @property {object} baseActivities - Actividades base/plantilla.
 * @property {boolean} isLoadingBaseActivities - Indica si se están cargando las actividades base.
 * @property {Array<object>} projectAuditLogs - Registros de auditoría del proyecto seleccionado.
 * @property {boolean} isLoadingAuditLogs - Indica si se están cargando los logs de auditoría.
 * @property {function(object)} fetchProjects - Carga proyectos del backend.
 * @property {function(object)} addProject - Añade un nuevo proyecto.
 * @property {function(string, object)} updateProject - Actualiza un proyecto existente.
 * @property {function(string)} deleteProject - Elimina un proyecto.
 * @property {function(string)} fetchProjectById - Carga los detalles de un proyecto por ID.
 * @property {function(object|null)} setSelectedProject - Establece el proyecto seleccionado.
 * @property {function(string)} syncActivities - Sincroniza actividades de un proyecto con la plantilla.
 * @property {function(string, boolean)} toggleActivityCompletion - Alterna el estado de completado de una actividad.
 * @property {function()} fetchBaseActivities - Carga las actividades base.
 * @property {function(object)} addBaseActivity - Añade una actividad base.
 * @property {function(string, object)} updateBaseActivity - Actualiza una actividad base.
 * @property {function(string)} deleteBaseActivity - Elimina una actividad base.
 * @property {function(string, string)} addProjectComment - Añade un comentario a un proyecto.
 * @property {function(string, FormData)} uploadProjectFile - Sube un archivo a un proyecto.
 * @property {function(string)} deleteProjectFile - Elimina un archivo de un proyecto.
 * @property {function(string)} fetchProjectAuditLogs - Carga los logs de auditoría para un proyecto.
 */

export const useProjectStore = create((set, get) => ({
  projects: [],
  isLoadingProjects: false,
  selectedProject: null,
  baseActivities: {},
  isLoadingBaseActivities: false,
  projectAuditLogs: [], // NUEVO: Estado para los logs de auditoría
  isLoadingAuditLogs: false, // NUEVO: Estado de carga para los logs

  fetchProjects: async (filters = {}) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para cargar proyectos.', 'error');
      return;
    }

    set({ isLoadingProjects: true });
    try {
      const projects = await apiGetAllProjects(token, filters);
      set({ projects });
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar proyectos.', 'error');
      set({ projects: [] });
    } finally {
      set({ isLoadingProjects: false });
    }
  },

  addProject: async (projectData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para añadir proyecto.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const response = await apiCreateProject(projectData, token);
      const newProject = response.project;
      set((state) => ({ projects: [newProject, ...state.projects] }));
      useUIStore.getState().showTemporaryMessage('Proyecto añadido con éxito.', 'success');
      return newProject;
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al añadir proyecto.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  updateProject: async (projectId, projectData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para actualizar proyecto.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const updatedProjectData = await apiUpdateProject(projectId, projectData, token);
      const updatedProject = updatedProjectData.project;

      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === projectId ? { ...p, ...updatedProject } : p
        ),
        selectedProject: state.selectedProject?.id === projectId ? { ...state.selectedProject, ...updatedProject } : state.selectedProject,
      }));
      useUIStore.getState().showTemporaryMessage('Proyecto actualizado con éxito.', 'success');
      return updatedProject;
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar proyecto.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  deleteProject: async (projectId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para eliminar proyecto.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      await apiDeleteProject(projectId, token);
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== projectId),
      }));
      useUIStore.getState().showTemporaryMessage('Proyecto eliminado con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar proyecto.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  fetchProjectById: async (projectId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para ver detalles del proyecto.', 'error');
      return;
    }

    set({ isLoadingProjects: true });
    try {
      const project = await apiGetProjectById(projectId, token);
      set({ selectedProject: project });
      return project;
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar detalles del proyecto.', 'error');
      set({ selectedProject: null });
      throw error;
    } finally {
      set({ isLoadingProjects: false });
    }
  },

  setSelectedProject: (project) => set({ selectedProject: project }),

  syncActivities: async (projectId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para sincronizar.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      // La respuesta ahora es un objeto { message, project }
      const response = await apiSyncProjectActivities(projectId, token);
      set({ selectedProject: response.project });
      // Usar el mensaje dinámico del backend
      useUIStore.getState().showTemporaryMessage(response.message, 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al sincronizar actividades.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  toggleActivityCompletion: async (activityId, isCompleted) => {
    const token = useAuthStore.getState().token;
    const currentProject = get().selectedProject;
    const currentProjectId = currentProject?.id;

    if (!token || !currentProjectId || !currentProject) {
      useUIStore.getState().showTemporaryMessage('No autenticado o proyecto no seleccionado.', 'error');
      return;
    }

    const updatedOptimisticProject = JSON.parse(JSON.stringify(currentProject));
    let originalProjectState = JSON.parse(JSON.stringify(currentProject));
    let updatedPhaseInStore = null;

    updatedOptimisticProject.phases = updatedOptimisticProject.phases.map(phase => {
      const activityIndex = phase.activities.findIndex(a => a.id === activityId);
      if (activityIndex > -1) {
        phase.activities[activityIndex].isCompleted = isCompleted;
        const totalActivities = phase.activities.length;
        const completedActivities = phase.activities.filter(act => act.isCompleted).length;
        phase.progress = totalActivities > 0 ? parseFloat(((completedActivities / totalActivities) * 100).toFixed(2)) : 0;
        updatedPhaseInStore = phase;
      }
      return phase;
    });

    let totalPhasesForProgress = 0;
    let sumOfPhaseProgress = 0;
    let newRealizedAmount = 0;
    updatedOptimisticProject.phases.forEach(phase => {
      if (phase.activities.length > 0) {
        totalPhasesForProgress++;
        sumOfPhaseProgress += phase.progress;
      }
      if (phase.progress === 100 && typeof parseFloat(phase.expectedAmount) === 'number' && !isNaN(parseFloat(phase.expectedAmount))) {
        newRealizedAmount += parseFloat(phase.expectedAmount);
      }
    });
    updatedOptimisticProject.overallProgress = totalPhasesForProgress > 0 ? parseFloat((sumOfPhaseProgress / totalPhasesForProgress).toFixed(2)) : 0;
    updatedOptimisticProject.realizedAmount = parseFloat(newRealizedAmount.toFixed(2));

    set({ selectedProject: updatedOptimisticProject });

    try {
      const backendResponse = await apiUpdatePhaseActivity(activityId, isCompleted, token);
      set((state) => {
        if (state.selectedProject?.id === currentProjectId) {
          const finalUpdatedProject = { ...state.selectedProject };
          finalUpdatedProject.phases = finalUpdatedProject.phases.map(phase => {
            if (phase.id === updatedPhaseInStore.id) {
              const activityIdx = phase.activities.findIndex(a => a.id === activityId);
              if (activityIdx > -1) {
                phase.activities[activityIdx].isCompleted = backendResponse.activity.isCompleted;
              }
              phase.progress = backendResponse.phaseProgress;
            }
            return phase;
          });
          finalUpdatedProject.overallProgress = backendResponse.overallProjectProgress;
          finalUpdatedProject.realizedAmount = backendResponse.projectRealizedAmount;
          return { selectedProject: finalUpdatedProject };
        }
        return state;
      });
      useUIStore.getState().showTemporaryMessage('Actividad actualizada.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar actividad.', 'error');
      set({ selectedProject: originalProjectState });
    }
  },

  fetchBaseActivities: async () => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para cargar actividades base.', 'error');
      return;
    }

    set({ isLoadingBaseActivities: true });
    try {
      const activities = await apiGetBaseActivities(token);
      set({ baseActivities: activities });
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar actividades base.', 'error');
      set({ baseActivities: {} });
    } finally {
      set({ isLoadingBaseActivities: false });
    }
  },

  addBaseActivity: async (activityData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para añadir actividad base.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const newActivity = await apiAddBaseActivity(activityData, token);
      await get().fetchBaseActivities();
      useUIStore.getState().showTemporaryMessage('Actividad base añadida con éxito.', 'success');
      return newActivity;
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al añadir actividad base.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  updateBaseActivity: async (activityId, activityData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para actualizar actividad base.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      await apiUpdateBaseActivity(activityId, activityData, token);
      await get().fetchBaseActivities();
      useUIStore.getState().showTemporaryMessage('Actividad base actualizada con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar actividad base.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  deleteBaseActivity: async (activityId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para eliminar actividad base.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      await apiDeleteBaseActivity(activityId, token);
      await get().fetchBaseActivities();
      useUIStore.getState().showTemporaryMessage('Actividad base eliminada con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar actividad base.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  addProjectComment: async (projectId, text) => {
    const token = useAuthStore.getState().token;
    useUIStore.getState().setIsLoading(true);
    try {
      const newComment = await apiAddComment(projectId, text, token);
      set((state) => ({
        selectedProject: {
          ...state.selectedProject,
          comments: [...state.selectedProject.comments, newComment],
        },
      }));
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al añadir comentario.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  uploadProjectFile: async (projectId, formData) => {
    const token = useAuthStore.getState().token;
    useUIStore.getState().setIsLoading(true);
    try {
      const newFile = await apiUploadFile(projectId, formData, token);
      set((state) => {
        const otherFiles = state.selectedProject.files.filter(
          (file) => !(file.phaseName === newFile.phaseName && file.documentType === newFile.documentType)
        );
        return {
          selectedProject: {
            ...state.selectedProject,
            files: [...otherFiles, newFile],
          },
        };
      });
      useUIStore.getState().showTemporaryMessage('Archivo subido con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al subir archivo.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  deleteProjectFile: async (fileId) => {
    const token = useAuthStore.getState().token;
    useUIStore.getState().setIsLoading(true);
    try {
      await apiDeleteFile(fileId, token);
      set((state) => ({
        selectedProject: {
          ...state.selectedProject,
          files: state.selectedProject.files.filter((file) => file.id !== fileId),
        },
      }));
      useUIStore.getState().showTemporaryMessage('Archivo eliminado.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar archivo.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  // NUEVO: Acción para obtener los logs de auditoría de un proyecto
  fetchProjectAuditLogs: async (projectId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para cargar el historial del proyecto.', 'error');
      return;
    }

    set({ isLoadingAuditLogs: true });
    try {
      const logs = await apiGetProjectAuditLogs(projectId, token);
      set({ projectAuditLogs: logs });
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar el historial del proyecto.', 'error');
      set({ projectAuditLogs: [] });
    } finally {
      set({ isLoadingAuditLogs: false });
    }
  },
}));
